import { useTellescopeQuery } from "@tellescope/tanstack-query";

// Stripe subscription types based on the proxy_read response
export interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  metadata?: Record<string, string>;
  images?: string[];
}

export interface StripePlan {
  id: string;
  active: boolean;
  amount: number; // Amount in cents
  currency: string;
  interval: "day" | "week" | "month" | "year";
  interval_count: number;
  nickname?: string;
  product: StripeProduct;
}

export interface StripePaymentMethod {
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}

export interface StripeSubscription {
  id: string;
  status: "active" | "past_due" | "unpaid" | "canceled" | "incomplete" | "incomplete_expired" | "trialing" | "paused";
  current_period_start: number; // Unix timestamp
  current_period_end: number; // Unix timestamp
  cancel_at_period_end: boolean;
  canceled_at?: number;
  ended_at?: number;
  plan: StripePlan;
  default_payment_method?: StripePaymentMethod;
  // pause_collection is set when subscription billing is paused (status stays "active")
  pause_collection?: {
    behavior: "keep_as_draft" | "mark_uncollectible" | "void";
    resumes_at?: number | null;
  } | null;
}

/**
 * Helper to check if a subscription has paused billing.
 * Stripe's pause_collection feature keeps status as "active" but sets pause_collection.
 */
export function isSubscriptionPaused(subscription: StripeSubscription): boolean {
  return subscription.pause_collection != null || subscription.status === "paused";
}

export interface StripeSubscriptionsResponse {
  subscriptions: StripeSubscription[];
}

export interface UseStripeSubscriptionsOptions {
  limit?: number;
}

export function useStripeSubscriptions(options: UseStripeSubscriptionsOptions = {}) {
  const { limit = 20 } = options;

  const query = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Stripe",
      type: "subscriptions",
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - same as default
    select: (response) => (response?.data as StripeSubscriptionsResponse) ?? { subscriptions: [] },
  });

  // Extract and limit subscriptions
  const allSubscriptions = Array.isArray(query.data?.subscriptions) ? query.data.subscriptions : [];
  const subscriptions = allSubscriptions.slice(0, limit);

  return {
    ...query,
    subscriptions,
  };
}
